import { DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import { Component, inject, input, model } from '@angular/core';
import { Notification } from 'src/app/interfaces/notification';
import { NotificationsService } from 'src/app/services/Notifications/notifications.service';

@Component({
  selector: 'notification-link',
  imports: [
    DatePipe,
    NgClass,
  ],
  templateUrl: './notification-link.component.html',
  styleUrl: './notification-link.component.scss'
})
export class NotificationLinkComponent {
  // injectables
  notificationApi = inject(NotificationsService)

  // inputs
  notification = model.required<Notification>();
  linkPrefix = input.required<string>();
  role = input<string>();

  item!: NotificationLink;

  constructor() { }
  ngOnInit() {
    this.item = this.getNotificationDetails();
  }
  getNotificationDetails(): NotificationLink {
    switch(this.notification().type) {
      case 'product_concerns':
        const fae = this.notification().data.fae_details
        const products = Object.keys(this.notification().data.concern_details.products).length
        return {
          title: 'Product concern received!',
          icon: 'bi-box',
          description: `${new TitleCasePipe().transform(fae.first_name + " " + fae.last_name)} submitted concerns on (${products}) product/s, check your email for more info.`,
          url: `concerns?search=${this.notification().data.concern_details.reference}&filter=pending`,
          created_at: this.notification().created_at
        }
      case 'product_concerns_resolved':
        const product_admin = this.notification().data.product_admin_details
        const ref = this.notification().data.concern_details.reference
        return {
          title: 'Product concern resolved!',
          icon: 'bi-box',
          description: `${new TitleCasePipe().transform(product_admin.first_name + " " + product_admin.last_name)} closed a concern(${ref}).`,
          url: `concerns?search=${ref}&filter=resolved`,
          created_at: this.notification().created_at
        }
      case 'quotation_created':
        return {
          title: 'New quotation request',
          icon: 'bi-file-earmark-plus-fill',
          description: this.getNotificationDescription(),
          url: `quotation/${this.notification().data.quotation_details?.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'quotation_updated':
        return {
          title: 'Updated quotation request',
          icon: 'bi-pen-fill',
          description: `An existing quotation request with ${this.notification().data.quotation_reference} quotation reference has been updated. `,
          url: `quotation/${this.notification().data.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'quotation_cancelled':
        return {
          title: 'Quotation request cancelled',
          icon: 'bi-file-earmark-x-fill',
          description: `User ${this.getCustomerName()} has cancelled a quotation with ${this.notification().data.quotation_details?.quotation_reference} quotation reference.`,
          url: `quotation/${this.notification().data.quotation_details?.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'quotation_approval':
        return {
          title: 'Quotation needing approval',
          icon: 'bi-tags-fill',
          description: `A new quotation request with ${this.notification().data.quotation_reference} quotation reference is pending for your approval.`,
          url: `quotation/${this.notification().data.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'quotation_approved':
        return {
          title: 'Quotation approved',
          icon: 'bi-file-earmark-check-fill',
          description: `Your quotation request with ${this.notification().data.quotation_reference} quotation reference is approved.`,
          url: `quotation/${this.notification().data.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'customer_registration':
        return {
          title: 'New User pending account approval',
          icon: 'bi-person-plus-fill',
          description: `Please proceed to the user management page to review and manage the request.`,
          url: `clients`,
          created_at: this.notification().created_at
        }
      case 'quotation_generated':
        return {
          title: 'Quotation generated',
          icon: 'bi-file-earmark-text-fill',
          description: `A new quotation file with ${this.notification().data.quotation_reference} reference has been generated.`,
          url: `quotation/${this.notification().data.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'customer_assigned':
        return {
          title: 'New customer assigned',
          icon: 'bi-file-person-fill',
          description: `A new customer has been assigned to you by the Product Admin`,
          url: `clients`,
          created_at: this.notification().created_at
        }
      case 'quotation_revision':
        return {
          title: 'Quotation request needs revision',
          icon: 'bi-pencil-square',
          description: `The quotation request with ${this.notification().data.quotation_reference} is needing revision.`,
          url: `quotation/${this.notification().data.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'product_approved':
        return {
          title: 'Assigned product approved',
          icon: 'bi-person-fill-check',
          description: `The product assigned to you has been approved by ${this.notification().data.product_admin?.name ?? 'a product admin'}.`,
          url: `product/${this.notification().data.product_uuid}`,
          created_at: this.notification().created_at
        }
      case 'product_created':
        return {
          title: 'Product needing approval',
          icon: 'bi-box',
          description: `A new product is pending for your approval.`,
          url: `product/${this.notification().data.product_uuid}`,
          created_at: this.notification().created_at
        }
      case 'customer_no_fae':
        return {
          title: 'New user needing FAE Approval',
          icon: 'bi-file-earmark-person-fill',
          description: `A new user has been created and requires FAE assignment.`,
          url: `clients`,
          created_at: this.notification().created_at
        }
      case 'request_created':
        return {
          title: 'Quotation created',
          icon: 'bi-file-earmark-plus-fill',
          description: `Your assigned sales representative, ${this.getFaeName()}, has created a quotation request for you. Please proceed to the quotation page to review the details.`,
          url: `quotation/${this.notification().data.quotation_details.quotation_uuid}`,
          created_at: this.notification().created_at
        }
      case 'quotation_ready':
        return {
          title: 'Quotation ready',
          icon: 'bi-file-earmark-text-fill',
          description: `Your assigned sales representative, has completed a quotation request for you. Please proceed to the quotation page to review the details.`,
          url: `quotation/${
            this.notification().data.quotation_uuid
          }`,
          created_at: this.notification().created_at,
        };
      case'client_transfer':
        return {
          title: 'Client Transfered',
          icon: 'bi-file-earmark-text-fill',
          description: `A new client named ${
            this.notification().data.customer_details.name
          } has been assigned to you.`,
          url: `clients/${this.notification().data.customer_details.uuid}/edit`,
          created_at: this.notification().created_at,
        };
      case'client_transfer_away':
        return {
          title: 'Client Release',
          icon: 'bi-file-earmark-text-fill',
          description: `Your previous client named ${this.notification().data.customer_details.name} has been transfered to another FAE.`,
          url: `clients/${this.notification().data.customer_details.uuid}/edit`,
          created_at: this.notification().created_at,
        };
      default: return {} as NotificationLink;
    }
  }

  getNotificationDescription(): string {
    const type = this.notification().type;
    const role = this.role()!;
    let messages: Record<string, string>;

    if (type === 'quotation_created') {
      messages = {
        fae: `User ${this.getCustomerName()} has requested a quotation. Please proceed to the quotation page to review the details.`,
        tsm: `The product assigned to you has been requested for quotation. Please proceed to the quotation page to review the details.`
      };
      return messages[role] || '';
    }

    return '';
  }

  getFaeName(): string {
    const titleCase = new TitleCasePipe();
    return titleCase.transform(`${this.notification().data.fae_details.first_name} ${this.notification().data.fae_details.last_name}`);
  }

  getCustomerName(): string {
    const titleCase = new TitleCasePipe();
    return titleCase.transform(`${this.notification().data.customer_details.first_name} ${this.notification().data.customer_details.last_name}`);
  }

  isNotifValid(item: NotificationLink): boolean {
    return item && Object.values(item).some(val => val !== null || val !== undefined || val !== '');
  }

  read() {
    this.notificationApi.readNotification(this.notification().uuid).subscribe({
      next: (val) => this.notification.update(el => ({...el, val})),
      complete: () => window.location.href = `/${this.linkPrefix()}/${this.item.url}`
    });
  }
}

export interface NotificationLink{
  title: string;
  description: string,
  icon: string,
  url: string,
  created_at: string,
}






